import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classes from './Resources360.module.scss';
import { BannerDescription } from 'new/elements/BannerDescription';
import { ResourceSearch } from 'new/elements/ResourceSearch';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/Typography';
import { resourceFetchActions } from 'new/components/monitor/ResourceTriage/ResourceTriage';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import { useDispatch, useSelector } from 'react-redux';
import { useResource } from 'new/components/monitor/ResourceTriage/useResource';
import { Tabs } from 'new/elements/Tabs';
import { TabProps } from 'new/elements/Tabs/Tabs';
import { Resume } from 'new/components/monitor/ResourceTriage/components/Resume';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { ROUTE_RESOURCES_360 } from 'new/constants/pathnames';
import { Overview } from 'new/elements/Overview';
import cx from 'classnames';
import { Access } from 'new/components/collaborate/Resources360/Access';
import { fetchAcl, fetchOrgMembers, loadMyTeams } from 'js/actions';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { aclTypes } from 'new/components/collaborate/ResourceDetail/ResourceDetail';
import { ACL_RESOURCE_TYPE_DATASET } from 'js/constants/aclConstants';
import { RESOURCE_TYPE_DATA_SET } from 'js/constants/resourceTypes';
import { getEffectiveRole } from 'js/lib/ACLUtils';
import { useResourceAclItems } from 'new/hooks/adapters/useResourceAclItems';
import { ModalFlowPreview } from 'new/elements/ModalFlowPreview';
import { Link, useHistory } from 'react-router-dom';
import useUpdateSearch from 'new/elements/ResourceSearch/useUpdateSearch';
import { useModals } from 'new/hooks/useModals';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_ACL_REQUEST } from 'js/constants/actionTypes';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import {
  RESOURCE_TYPE_FLOW,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import { EmptyState } from 'new/elements/EmptyState/EmptyState';

interface Resources360Props {}

const optionsToHide = [RESOURCE_TYPE_SINK, RESOURCE_TYPE_SOURCE];

const description =
  'Manage Resource access, ensuring the smooth exchange of data and effective collaboration.';

const tabs = [
  {
    value: 'overview',
    label: 'Overview',
    Component: Overview,
  },
  {
    value: 'access',
    label: 'Access',
    Component: Access,
  },
] as (TabProps & { Component: any })[];

export const Resources360 = (props: Resources360Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { closeModal } = useModals();
  const requested = useRef(false);
  const [tab, setTab] = useState(tabs[0]);
  const { type, id, setData } = useUpdateSearch();

  const isNexset = type === RESOURCE_TYPE_DATA_SET;
  const isFlow = type === RESOURCE_TYPE_FLOW;
  // For Nexsets Share tab, use DATASET type; for others use aclTypes mapping
  const aclType = isNexset ? ACL_RESOURCE_TYPE_DATASET : aclTypes[type];

  const org = useCurrentOrg();

  const { resource, loading: loadingResource } = useResource(type, id);
  const loadingAcl = useIsLoading(GET_ACL_REQUEST);
  const loading = loadingAcl || loadingResource;

  // For Flows, ensure flow_node_id is set for useResourceAclItems key lookup
  // (Flow objects have id/origin_node_id but not flow_node_id)
  const resourceForAcl = useMemo(() => {
    if (!resource) return null;
    if (isFlow) {
      return { ...resource, flow_node_id: resource.id };
    }
    return resource;
  }, [resource, isFlow]);

  // Get ACL items - useResourceAclItems for entity enrichment (name, email, etc.)
  const { aclItems: enrichedAclItems, owner } = useResourceAclItems(
    !isNexset ? resourceForAcl : null,
    !isNexset ? aclType : null,
  );

  // Get raw ACL items from Redux to preserve original access_roles
  const byKey = useSelector((state: any) => state.acl?.byKey || {});

  // For Nexsets, get datasetAclItems directly from Redux (matches AclTabViewContainer.js)
  const datasetAclItems = useMemo(() => {
    if (!isNexset || !resource?.id) return [];
    const key = `${ACL_RESOURCE_TYPE_DATASET}${resource.id}`;
    return byKey[key] || [];
  }, [isNexset, resource?.id, byKey]);

  // For non-Nexsets, merge enriched items with original access_roles from raw Redux data
  const flowAclItems = useMemo(() => {
    if (isNexset || !resourceForAcl) return [];
    const keyId = isFlow ? resourceForAcl.flow_node_id : resourceForAcl.id;
    const rawItems = byKey[`${aclType}${keyId}`] || [];

    // Merge: use enriched item for entity info, but preserve access_roles from raw
    return enrichedAclItems.map((enrichedItem) => {
      const rawItem = rawItems.find(
        (r) => r.id === enrichedItem.id && r.type === enrichedItem.type,
      );
      return {
        ...enrichedItem,
        // Preserve original access_roles from raw ACL data
        access_roles: rawItem?.access_roles || enrichedItem.access_roles,
      };
    });
  }, [isNexset, isFlow, resourceForAcl, aclType, byKey, enrichedAclItems]);

  // For Nexsets, filter to only show 'sharer' role (matches AclTabView.js behavior)
  const aclItems = useMemo(() => {
    if (!isNexset) {
      return flowAclItems;
    }
    return datasetAclItems.filter((item) => getEffectiveRole(item) === 'sharer');
  }, [isNexset, flowAclItems, datasetAclItems]);

  useEffect(() => {
    dispatch(fetchOrgMembers(org.id));
    dispatch(loadMyTeams());
  }, [org.id]);

  useEffect(() => {
    if (type && id) {
      const action = resourceFetchActions[type];
      if (action) {
        dispatch(action(id, ACLQueryModeTypes.MODE_COLLABORATOR.key));
      }
    }
  }, [type, id]);

  useEffect(() => {
    if (resource) {
      // For Nexsets, only fetch data_set accessors (sharers)
      // For other resources, fetch their respective ACL type
      dispatch(fetchAcl(aclType, resource));
    }
  }, [resource, aclType]);

  const onTabChange = (e, value) => {
    setTab(tabs.find((tab) => tab.value === value));
  };

  const onItemClick = useCallback((type, data) => {
    closeModal(ModalFlowPreview.modalConfig.name);
    setData({ type, id: data.id });
    const params = new URLSearchParams(history.location.search);
    params.set('type', type);
    params.set('id', data.id);
    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });
  }, []);

  const Component = tab?.Component || React.Fragment;

  return (
    <>
      <BannerDescription title="Resources 360°" description={description} />
      <div className={classes.dividerContainer}>
        <Stack width="70%" className={classes.searchContainer}>
          <ResourceSearch
            hideOptions={optionsToHide}
            type={type}
            id={id}
            fullWidth
            onSearch={(data) => {
              requested.current = true;
              setData(data);
            }}
          />
        </Stack>
      </div>

      {loading && <CenteredLoader height={100} />}

      {!requested.current && !loading && !resource && (
        <EmptyState
          title="Find a Resource to Begin"
          description="Select a Resource Type and enter a Resource ID to manage access for that resource."
          tip={
            <>
              Need help finding a Resource ID?{' '}
              <Link to="/integrate/flows" target="_blank">
                Check your Data Flows
              </Link>{' '}
              (opens in new tab) or ask your admin.
            </>
          }
        />
      )}

      {!!resource && !loading && (
        <Stack direction="row" height="100%">
          <Stack width="70%" className={classes.contentContainer}>
            {!!owner?.full_name && (
              <div className={cx(classes.dividerContainer, classes.ownerContainer)}>
                <Typography>
                  Owned by <Typography variant="button2">{owner?.full_name}</Typography>
                </Typography>
              </div>
            )}
            {type === RESOURCE_TYPE_FLOW ? (
              <Access
                resourceId={id}
                resourceType={type}
                data={resource}
                alreadyShared={aclItems}
              />
            ) : (
              <>
                <div className={cx(classes.dividerContainer, classes.tabsContainer)}>
                  <Tabs value={tab.value} tabs={tabs} onChange={onTabChange} />
                </div>
                <Component
                  resourceType={type}
                  resourceId={id}
                  data={resource}
                  alreadyShared={aclItems}
                />
              </>
            )}
          </Stack>
          <Stack width="30%">
            <Resume {...resource} onFlowItemClicked={onItemClick} resourceType={type} id={id} />
          </Stack>
        </Stack>
      )}
    </>
  );
};

Resources360.screenConfig = {
  path: `${ROUTE_REDESIGN}${ROUTE_RESOURCES_360}`,
  domain: 'collaborate',
  modals: [ModalFlowPreview],
  header: {
    title: 'Resources 360º',
    slugTitle: 'Resources 360º',
  },
  screenName: 'Resources360',
};
